import axios from "axios";
import { EventBus } from "../components/EventBus";
import { NotificationManager } from "react-notifications";

/**
 * SISTEMA DE LOADING GLOBAL E RETRY AUTOMÁTICO
 * 
 * Este sistema gerencia automaticamente o loading global da aplicação e
 * implementa retry automático para pedidos que falhem.
 * 
 * Como usar:
 * 
 * 1. AUTOMÁTICO (Recomendado):
 *    - Todas as requisições axios feitas através desta instância mostram automaticamente o loading
 *    - O loading é controlado por um contador de requisições ativas
 *    - Pedidos que retornem status diferente de 200 OK são automaticamente repetidos
 *    - Máximo de 3 tentativas com intervalo de 1 segundo entre cada tentativa
 *    
 * 2. COM TEXTO PERSONALIZADO:
 *    axiosInstance.get('/api/data', { loadingText: 'A carregar dados...' })
 *    
 * 3. OPTAR POR FORA (em casos especiais):
 *    axiosInstance.get('/api/data', { skipGlobalLoading: true })
 *    
 * 4. CONTROLO MANUAL:
 *    import { showGlobalLoading, hideGlobalLoading } from './axiosInstance'
 *    
 * 5. COM HOOK (componentes React):
 *    import { useGlobalLoading } from '../hooks/useGlobalLoading'
 * 
 * SISTEMA DE RETRY:
 * - Todos os pedidos que retornem status diferente de 200 OK são automaticamente repetidos
 * - Máximo de 3 tentativas no total (1 inicial + 2 retries)
 * - Intervalo de 1 segundo entre cada tentativa
 * - Após 3 tentativas falhadas, o erro é retornado normalmente
 * 
 */

// Counter to track active requests
let activeRequests = 0;

const decreaseActiveRequests = () => {
  activeRequests--;
  if (activeRequests < 1) {
    manageGlobalLoading(false);
  }
};

const axiosInstance = axios.create({
  baseURL: 'https://eposgestofinal.com/api',
  withCredentials: true, // Important for automatic cookie handling
  headers: {
    'Content-Type': 'application/json',
  },
});

// Helper function to get cookies
function getCookie(name) {
  const cookies = document.cookie.split(";");
  for (let i = 0; i < cookies.length; i++) {
    const cookie = cookies[i].trim();
    const [cookieName, value] = cookie.split("=");
    if (cookieName === name) {
      return decodeURIComponent(value || '');
    }
  }
  return null;
}

// Helper function to set cookies with domain
function setCookie(name, value, days = 0) {
  const expires = days ? new Date(Date.now() + days * 24 * 60 * 60 * 1000).toUTCString() : '';
  const domain = window.location.hostname.includes('eposgestofinal.com') ? '.eposgestofinal.com' : '';
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; domain=${domain}`;
}

// Helper function to manage global loading
function manageGlobalLoading(isLoading, text = '') {
  EventBus.$emit("Loading", { active: isLoading, text });
}

// Helper function to update loading text only
function setGlobalLoadingTextInternal(text) {
  EventBus.$emit("Loading", { active: true, text });
}

// Helper function to delay execution
function delay(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

// Maximum number of retry attempts
const MAX_RETRY_ATTEMPTS = 3;
const RETRY_DELAY_MS = 1500; // 1.5 segundos

// Add a request interceptor to include token in every request
axiosInstance.interceptors.request.use(
  (config) => {
    // Skip loading management for requests that explicitly opt out
    if (config.skipGlobalLoading) {
      return config;
    }

    // Increment active requests counter and show loading
    activeRequests++;
    if (activeRequests === 1) {
      // Only show loading on the first request to avoid flickering
      manageGlobalLoading(true, config.loadingText || '');
    } else if (config.loadingText) {
      // Update loading text if this request has a custom text
      setGlobalLoadingTextInternal(config.loadingText);
    }

    // Get token from multiple sources (cookies first, then localStorage fallback)
    // Note: 'token' cookie is httpOnly, so we use 'token_js' instead
    const token = getCookie('token_js') || getCookie('token') || localStorage.getItem('token');
    
    if (token) {
      // Add token in multiple ways to ensure maximum compatibility with LibApiProtection.php
      config.headers['x-auth-token'] = token;
      config.headers['Authorization'] = `Bearer ${token}`;
      config.headers['x-epos-auth'] = token;
      
      
      // Add to query params for GET requests
      if (config.method === 'get') {
        if (!config.params) {
          config.params = {};
        }
      }
    }
    
    // Always include session cookie for cross-domain requests
    // Only set x-epos-auth with sessionId if token is not available
    const sessionId = getCookie('ePOS');
    if (sessionId && !token) {
      config.headers['x-epos-auth'] = sessionId;
    }
    
    return config;
  },
  (error) => {
    decreaseActiveRequests();
    return Promise.reject(error);
  }
);

// Add a response interceptor
axiosInstance.interceptors.response.use(
  function (response) {
    // Decrement active requests counter
    decreaseActiveRequests();

    // Verificar se a resposta é 200 OK
    if (response.status !== 200) {
      // Se não for 200, tratar como erro e fazer retry
      // Não decrementar activeRequests aqui, será feito no handleRetry se falhar
      return handleRetry(response.config, response.status);
    }

    // Skip loading management for requests that explicitly opt out
    if (response.config.skipGlobalLoading) {
      return response;
    }
    
    return response;
  },
  async function (error) {
    // Skip loading management for requests that explicitly opt out
    if (error.config && error.config.skipGlobalLoading) {
      return Promise.reject(error);
    }

    // Obter o status do erro (se existir)
    const status = error.response?.status;
    
    // Se houver resposta com status diferente de 200, tentar retry
    if (error.response && status !== 200) {
      // Não decrementar activeRequests aqui, será feito no handleRetry se falhar
      return handleRetry(error.config, status);
    }

    // Se não houver resposta (erro de rede, timeout, etc.), também tentar retry
    if (!error.response) {
      // Não decrementar activeRequests aqui, será feito no handleRetry se falhar
      return handleRetry(error.config, null);
    }

    // Decrement active requests counter on error (apenas se não for para retry)
    decreaseActiveRequests();

    if (error.response && error.response.status === 401) {
      console.warn('[axiosInstance] ⚠️ 401 Unauthorized - Token may be invalid or expired');
      console.warn('[axiosInstance] ℹ️ User should re-login manually');
      // Don't force logout - just log warning
      // handleUnauthorized();
    }
    return Promise.reject(error);
  }
);

// Função para lidar com retry de pedidos
async function handleRetry(config, status) {
  // Inicializar contador de tentativas se não existir
  config.__retryCount = config.__retryCount || 0;
  
  // Para status 508, continuar indefinidamente até obter outro status
  const isStatusServerCause = [508, 503].includes(status);
  
  // Se já excedeu o número máximo de tentativas E não for status 508, rejeitar
  if (!isStatusServerCause && config.__retryCount >= MAX_RETRY_ATTEMPTS) {
    console.error(`[axiosInstance] ❌ Falhou após ${MAX_RETRY_ATTEMPTS} tentativas. Status: ${status || 'sem resposta'}`);
    
    // Notificar o utilizador sobre o erro
    NotificationManager.error(
      'Tente fazer refresh da página. Caso o problema persista, contacte o administrador do sistema.',
      'Ocorreu um erro inesperado',
      8000 // 8 segundos para dar tempo ao utilizador ler a mensagem
    );
    
    // Decrement active requests counter apenas se não for skipGlobalLoading
    if (!config.skipGlobalLoading) {
      decreaseActiveRequests();
    }
    
    // Retornar erro apropriado
    const error = new Error(`Pedido falhou após ${MAX_RETRY_ATTEMPTS} tentativas`);
    error.config = config;
    error.response = {
      status: status || 0,
      statusText: 'Request Failed',
      data: { message: 'Pedido falhou após múltiplas tentativas' }
    };
    return Promise.reject(error);
  }
  
  // Incrementar contador de tentativas
  config.__retryCount++;
  
  if (isStatusServerCause) {
    console.warn(`[axiosInstance] ⚠️ Status 508 - Tentativa ${config.__retryCount}. A repetir em 1 segundo até obter outro status...`);
  } else {
    console.warn(`[axiosInstance] ⚠️ Status ${status || 'sem resposta'} - Tentativa ${config.__retryCount}/${MAX_RETRY_ATTEMPTS}. A repetir em 1 segundo...`);
  }
  
  // Decrementar activeRequests da tentativa anterior antes de fazer retry
  // (a nova tentativa vai incrementar novamente no request interceptor)
  if (!config.skipGlobalLoading) {
    decreaseActiveRequests();
  }
  
  // Aguardar 1 segundo antes de repetir
  await delay(RETRY_DELAY_MS);
  
  // Repetir o pedido (o request interceptor vai incrementar activeRequests novamente)
  // Se o resultado ainda for 508, o interceptor vai chamar handleRetry novamente
  return axiosInstance(config);
}

// function handleUnauthorized() {
//   // Clear all auth-related data
//   clearAuthData();
//   window.location.href = "https://eposgestofinal.com/index.php?logout=1";
// }

function clearAuthData() {
  // Clear localStorage
  localStorage.removeItem('token');
  localStorage.removeItem('public_indentifier');
  localStorage.removeItem('avatar');
  
  // Clear cookies
  const cookiesToClear = ['token', 'idempresa', 'avatar', 'public_indentifier', 'ePOS'];
  cookiesToClear.forEach(name => {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=.eposgestofinal.com`;
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
  });
}

// Helper functions for manual loading control
export function showGlobalLoading(text = '') {
  manageGlobalLoading(true, text);
}

export function hideGlobalLoading() {
  manageGlobalLoading(false);
}

export function setGlobalLoadingText(text) {
  setGlobalLoadingTextInternal(text);
}

export default axiosInstance;
export { getCookie, setCookie, clearAuthData };